import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { rv, vw, tabletUp } from '../../../shared/lib/responsive';

/** 구름 1장 — 오른쪽 → 왼쪽 (linear) */
const cloudPassLeft = keyframes`
  from {
    transform: translateX(calc(var(--screen-max-width) + var(--cloud-edge-offset)));
  }
  to {
    transform: translateX(calc(-100% - var(--cloud-edge-offset)));
  }
`;

export const Root = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  background-color: var(--color-bg-base);
`;

export const Decorations = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

/** 애니메이션 꺼짐 시 항상 보이는 고정 세트 */
export const CloudSetFixed = styled.div<{ $width: number; $left: number }>`
  position: absolute;
  top: 0;
  ${(p) => rv(p.$left, 'left')}
  ${(p) => rv(p.$width, 'width')}
  height: 100%;
  display: none;

  @media (prefers-reduced-motion: reduce) {
    display: block;
  }
`;

export const CloudMover = styled.div<{
  $width: number;
  $duration: string;
  $delay: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  ${(p) => rv(p.$width, 'width')}
  height: 100%;
  will-change: transform;
  animation: ${cloudPassLeft} ${({ $duration }) => $duration} linear infinite;
  animation-delay: ${({ $delay }) => $delay};

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const CloudInSet = styled.img<{
  $topPercent: number;
  $left: number;
  $width: number;
  $opacity?: number;
}>`
  position: absolute;
  top: ${({ $topPercent }) => $topPercent}%;
  ${(p) => rv(p.$left, 'left')}
  display: block;
  ${(p) => rv(p.$width, 'width')}
  max-width: ${({ $width }) => vw($width)};

  ${tabletUp} {
    max-width: ${({ $width }) => `${$width}px`};
  }

  height: auto;
  opacity: ${({ $opacity }) => $opacity ?? 1};
`;

export const WindDeco = styled.img<{
  $left: number;
  $topPercent: number;
  $width: number;
  $opacity?: number;
}>`
  position: absolute;
  ${(p) => rv(p.$left, 'left')}
  top: ${({ $topPercent }) => $topPercent}%;
  ${(p) => rv(p.$width, 'width')}
  height: auto;
  opacity: ${({ $opacity }) => $opacity ?? 0.85};
`;

export const MapleDecoFixed = styled.img<{
  $left: number;
  $topPercent: number;
  $width: number;
  $rotate?: number;
  $zIndex?: number;
  $opacity?: number;
}>`
  position: absolute;
  ${(p) => rv(p.$left, 'left')}
  top: ${({ $topPercent }) => $topPercent}%;
  ${(p) => rv(p.$width, 'width')}
  height: auto;
  z-index: ${({ $zIndex }) => $zIndex ?? 1};
  opacity: ${({ $opacity }) => $opacity ?? 1};
  transform: rotate(${({ $rotate }) => $rotate ?? 0}deg);
`;

const mapleWindFromRight = keyframes`
  0% {
    opacity: 0;
    transform: translate(var(--maple-wind-from-x), 0) rotate(var(--maple-rotate, 0deg));
  }
  20% {
    opacity: var(--maple-peak-opacity, 1);
    transform: translate(calc(var(--maple-wind-from-x) * 0.55), calc(var(--maple-wind-drop-end) * 0.2))
      rotate(var(--maple-rotate, 0deg));
  }
  58% {
    opacity: calc(var(--maple-peak-opacity, 1) * 0.72);
    transform: translate(var(--maple-wind-mid-x), calc(var(--maple-wind-drop-end) * 0.55))
      rotate(var(--maple-rotate, 0deg));
  }
  100% {
    opacity: 0;
    transform: translate(var(--maple-wind-to-x), var(--maple-wind-drop-end))
      rotate(var(--maple-rotate, 0deg));
  }
`;

export const MapleDeco = styled.img<{
  $left: number;
  $topPercent: number;
  $width: number;
  $rotate?: number;
  $zIndex?: number;
  $duration: string;
  $ease: string;
  $delay: string;
  $windFromX: number;
  $windMidX: number;
  $windToX: number;
  $windDropEnd: number;
  $opacity?: number;
}>`
  position: absolute;
  ${(p) => rv(p.$left, 'left')}
  top: ${({ $topPercent }) => $topPercent}%;
  ${(p) => rv(p.$width, 'width')}
  height: auto;
  z-index: ${({ $zIndex }) => $zIndex ?? 1};
  will-change: transform, opacity;
  --maple-rotate: ${({ $rotate }) => $rotate ?? 0}deg;
  --maple-wind-from-x: ${({ $windFromX }) => vw($windFromX)};
  --maple-wind-mid-x: ${({ $windMidX }) => vw($windMidX)};
  --maple-wind-to-x: ${({ $windToX }) => vw($windToX)};
  --maple-wind-drop-end: ${({ $windDropEnd }) => vw($windDropEnd)};
  --maple-peak-opacity: ${({ $opacity }) => $opacity ?? 1};

  ${tabletUp} {
    --maple-wind-from-x: ${({ $windFromX }) => `${$windFromX}px`};
    --maple-wind-mid-x: ${({ $windMidX }) => `${$windMidX}px`};
    --maple-wind-to-x: ${({ $windToX }) => `${$windToX}px`};
    --maple-wind-drop-end: ${({ $windDropEnd }) => `${$windDropEnd}px`};
  }

  animation: ${mapleWindFromRight} ${({ $duration }) => $duration} ${({ $ease }) => $ease}
    infinite;
  animation-delay: ${({ $delay }) => $delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.35;
  }
`;

export const DecoMapleVote = MapleDeco;
export const DecoMapleResult = MapleDeco;
export const DecoMapleAlt = MapleDeco;
